import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineMarkdown } from "@/components/InlineMarkdown";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  api,
  type HubAccountWithBindings,
  type HubTool,
  type IntegrationBinding,
  type ToolAccess,
  type ToolState,
} from "@/lib/api";
import { connectReturnTo, rememberConnecting } from "@/lib/connect-return";
import { useConnectReturn } from "@/hooks/useConnectReturn";
import {
  HUB_ACCOUNTS_KEY,
  canAddBinding,
  canRemoveBinding,
  hubAccountsKey,
  writableBindings,
} from "@/lib/hub-accounts";
import {
  apiAccessLevel,
  apiAccessRowCopy,
  displayedState,
  minimalToolAccess,
  readIncludedInFull,
  READ_INCLUDED_HINT,
  splitTools,
  toolAccessDiffers,
  toolsEditorCopy,
  toolsFooterSummary,
} from "@/lib/tool-access";
import { useBooConfigurable } from "@/lib/use-boo-configurable";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useRouterState, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Lock, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function appName(slug: string): string {
  return slug
    .split(/[_-]/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// Tool policy is stored as on/ask/off (see ToolAccess), but the "ask" flow is
// parked: the UI is a binary On/Off switch. "off" = off; anything else counts
// as on. Toggling only ever writes "on" or "off"; the tri-state stays in the
// schema for when the confirmation flow ships.
const isOn = (s: ToolState) => s !== "off";

type ManageSearch = { tab?: "tools" | "access"; boo?: string };

// Full-page management for one connected account: which Boos may use it (Access)
// and which of its tools they may run (Tools), plus disconnect and connecting
// another account of the same app. Reached from either integrations view via a
// per-account "Manage" button; `?boo=` says which Boo we came from (see the
// route). Keyed by the hub account id so it survives binding changes made on
// the page. What's editable follows the account's own flags — canManage (every
// binding), canShare (share / stop sharing), canDisconnect — plus the Boos the
// viewer configures.
export function ManageIntegrationPage() {
  useDocumentTitle("Manage integration");
  useConnectReturn();
  const { orgId, accountId } = useParams({ strict: false }) as { orgId?: string; accountId?: string };
  const { tab, boo } = useSearch({ strict: false }) as ManageSearch;
  const activeTab = tab === "access" ? "access" : "tools";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  // Drive the active tab from the URL so it's linkable; replace history so
  // flipping tabs doesn't stack back-button entries.
  const setTab = (t: "tools" | "access") => {
    if (!orgId || !accountId) return;
    navigate({
      to: "/orgs/$orgId/integrations/$accountId",
      params: { orgId, accountId },
      search: { tab: t, boo },
      replace: true,
    });
  };

  // Listed in the scope we came from: a Boo-private account only appears in
  // its home Boo's listing.
  const accountsQuery = useQuery({
    queryKey: hubAccountsKey(boo),
    queryFn: () => api.integrations.accounts(boo),
  });
  const account = accountsQuery.data?.accounts.find((a) => a.id === accountId);
  const orgCanManage = accountsQuery.data?._permissions?.canManage === true;

  const { query: containersQuery, configurable, names } = useBooConfigurable();
  const brains = (containersQuery.data?.data ?? []).filter((c) => c.agentType === "boo-brain");
  // Connecting another account takes the scope of where we came from: the
  // Boo (needs configure on it) or the workspace (org owner/admin).
  const canAddAccount = boo ? configurable.has(boo) : orgCanManage;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: HUB_ACCOUNTS_KEY });
  const goBack = () => {
    if (!orgId) return;
    if (boo) navigate({ to: "/orgs/$orgId/containers/$containerId/integrations", params: { orgId, containerId: boo } });
    else navigate({ to: "/orgs/$orgId/integrations", params: { orgId } });
  };

  const disconnect = useMutation({
    mutationFn: () => api.integrations.disconnect(account!.id),
    onSuccess: () => {
      invalidate();
      toast.success("Disconnected");
      goBack();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not disconnect"),
  });

  const displayName = account ? account.appName ?? appName(account.app) : "";
  const homeName = account?.homeContainerId ? names.get(account.homeContainerId) : undefined;

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col gap-6 p-6">
      <div>
        {orgId ? (
          <Button asChild size="sm" variant="ghost" className="-ml-2 gap-1.5">
            {boo ? (
              <Link to="/orgs/$orgId/containers/$containerId/integrations" params={{ orgId, containerId: boo }}>
                <ArrowLeft className="size-4" />
                {names.get(boo) ?? "Boo"} integrations
              </Link>
            ) : (
              <Link to="/orgs/$orgId/integrations" params={{ orgId }}>
                <ArrowLeft className="size-4" />
                Integrations
              </Link>
            )}
          </Button>
        ) : null}
      </div>

      {accountsQuery.isLoading ? (
        <LoadingIndicator />
      ) : accountsQuery.isError ? (
        <InlineError error={accountsQuery.error} />
      ) : !account ? (
        <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
          This connection no longer exists, or it is private to a Boo you don’t have access to.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            {account.logoUrl ? (
              <img src={account.logoUrl} alt="" className="size-10 shrink-0 rounded object-contain" />
            ) : (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-sm font-semibold">
                {monogram(displayName)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-lg font-semibold">{displayName}</h1>
              <p className="truncate text-sm text-muted-foreground">{account.label}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {canAddAccount && (
                <ConnectAnotherButton app={account.app} appLabel={displayName} boo={boo} />
              )}
              {account.canDisconnect && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-destructive hover:text-destructive"
                  onClick={() => setDisconnectOpen(true)}
                >
                  <Trash2 className="size-4" />
                  Disconnect
                </Button>
              )}
            </div>
          </div>

          <ScopeLine account={account} homeName={homeName} />

          <Tabs
            value={activeTab}
            onValueChange={(v) => setTab(v as "tools" | "access")}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TabsList>
              <TabsTrigger value="tools">Tools</TabsTrigger>
              <TabsTrigger value="access">Access</TabsTrigger>
            </TabsList>

            <TabsContent value="tools" className="mt-4 flex min-h-0 flex-1 flex-col">
              <ToolsTab account={account} configurable={configurable} onChanged={invalidate} />
            </TabsContent>

            <TabsContent value="access" className="mt-4">
              <AccessTab
                account={account}
                brains={brains}
                configurable={configurable}
                homeName={homeName}
                onChanged={invalidate}
              />
            </TabsContent>
          </Tabs>

          <ConfirmDialog
            open={disconnectOpen}
            onOpenChange={setDisconnectOpen}
            title={`Disconnect ${displayName}?`}
            description={
              account.scope === "boo"
                ? `${homeName ?? "This Boo"} loses this account. The sign-in is revoked where the app supports it.`
                : "Access is revoked for your whole workspace — every Boo using this account loses it."
            }
            confirmLabel="Disconnect"
            loading={disconnect.isPending}
            onConfirm={() => disconnect.mutate()}
          />
        </>
      )}
    </div>
  );
}

// Who can see and use the account, right under the header so the Access tab
// isn't the first place scope shows up.
function ScopeLine({ account, homeName }: { account: HubAccountWithBindings; homeName?: string }) {
  if (account.scope === "boo") {
    return (
      <Badge variant="outline" className="w-fit gap-1.5 text-muted-foreground">
        <Lock className="size-3" />
        Private to {homeName ?? "its Boo"}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="w-fit gap-1.5 text-muted-foreground">
      <Users className="size-3" />
      {homeName ? `Shared with the workspace from ${homeName}` : "Shared with the workspace"}
    </Badge>
  );
}

// Connect another account of the same app (e.g. a second Gmail). Leaves for
// the hub's connect page in the scope we came from (private to the Boo, or
// workspace); boo-backend binds the new account there and sends the browser
// back to this page, where the new account shows in the list behind the back
// link.
function ConnectAnotherButton({ app, appLabel, boo }: { app: string; appLabel: string; boo?: string }) {
  const location = useRouterState({ select: (s) => s.location });
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const { url } = await api.integrations.connectLink({
        app,
        containerId: boo ?? null,
        returnTo: connectReturnTo(location.pathname, location.searchStr),
      });
      rememberConnecting(appLabel);
      window.location.assign(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start the connection");
      setBusy(false);
    }
  };

  return (
    <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => void connect()}>
      <Plus className="size-4" />
      {busy ? "Connecting…" : "Add account"}
    </Button>
  );
}

// Which Boos may use this account. A private account has exactly one place
// it can live — its home Boo — plus the way out: sharing it with the
// workspace. A workspace account offers every Boo, org-wide or one by one; a
// switch is live for the direction it would go — adding needs configure on
// that Boo, removing also works for whoever manages the account. New bindings
// inherit the account's current purpose + tool policy so a newly-allowed Boo
// matches the others.
function AccessTab({
  account,
  brains,
  configurable,
  homeName,
  onChanged,
}: {
  account: HubAccountWithBindings;
  brains: { id: string; name: string }[];
  configurable: ReadonlySet<string>;
  homeName?: string;
  onChanged: () => void;
}) {
  const orgBinding = account.bindings.find((b) => b.containerId === null);
  const orgWide = orgBinding !== undefined;
  const template = orgBinding ?? account.bindings[0];
  const [confirmUnshare, setConfirmUnshare] = useState(false);

  const fail = (fallback: string) => (err: unknown) =>
    toast.error(err instanceof Error ? err.message : fallback);
  const bind = useMutation({
    mutationFn: (containerId: string | null) =>
      api.integrations.bind({
        hubAccountId: account.id,
        containerId,
        // Inherit so a new binding isn't silently more permissive than the rest.
        toolAccess: template?.toolAccess,
        purpose: template?.purpose ?? undefined,
      }),
    onSuccess: onChanged,
    onError: fail("Could not update access"),
  });
  const unbind = useMutation({
    mutationFn: (bindingId: string) => api.integrations.unbind(bindingId),
    onSuccess: onChanged,
    onError: fail("Could not update access"),
  });
  const share = useMutation({
    mutationFn: () => api.integrations.share(account.id),
    onSuccess: () => {
      toast.success("Shared with the workspace");
      onChanged();
    },
    onError: fail("Could not share"),
  });
  const unshare = useMutation({
    mutationFn: () => api.integrations.unshare(account.id),
    onSuccess: () => {
      setConfirmUnshare(false);
      toast.success("No longer shared");
      onChanged();
    },
    onError: fail("Could not stop sharing"),
  });
  const busy = bind.isPending || unbind.isPending || share.isPending || unshare.isPending;

  if (account.scope === "boo") {
    const home = account.homeContainerId;
    const homeBinding = home ? account.bindings.find((b) => b.containerId === home) : undefined;
    const canToggleHome =
      home !== null &&
      (homeBinding
        ? canRemoveBinding(account, home, configurable)
        : canAddBinding(account, home, configurable));
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 p-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">Private to {homeName ?? "this Boo"}</p>
            <p className="text-xs text-muted-foreground">
              Only this Boo can use the account. Share it to let other Boos in your workspace use it too.
            </p>
          </div>
          {account.canShare && (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 gap-1.5"
              disabled={busy}
              onClick={() => share.mutate()}
            >
              <Users className="size-3.5" />
              Share with the whole workspace
            </Button>
          )}
        </div>
        <div className="divide-y rounded-lg border">
          <div className="flex items-center justify-between gap-3 p-4">
            <p className="min-w-0 truncate text-sm">{homeName ?? "This Boo"}</p>
            <Switch
              checked={homeBinding !== undefined}
              disabled={!canToggleHome || busy}
              onCheckedChange={() =>
                homeBinding ? unbind.mutate(homeBinding.id) : home && bind.mutate(home)
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border bg-muted/30 p-4">
        <div className="min-w-0">
          <p className="text-sm font-medium">All Boos</p>
          <p className="text-xs text-muted-foreground">
            Every Boo in your workspace can use this account, including ones you add later.
          </p>
        </div>
        <Switch
          checked={orgWide}
          disabled={!account.canManage || busy}
          onCheckedChange={() => (orgBinding ? unbind.mutate(orgBinding.id) : bind.mutate(null))}
        />
      </label>

      <div>
        <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">
          {orgWide ? "Covered by All Boos" : "Or pick specific Boos"}
        </p>
        <div className="divide-y rounded-lg border">
          {brains.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No Boos yet.</p>
          ) : (
            brains.map((brain) => {
              const booBinding = account.bindings.find((b) => b.containerId === brain.id);
              const canToggle = booBinding
                ? canRemoveBinding(account, brain.id, configurable)
                : canAddBinding(account, brain.id, configurable);
              return (
                <div
                  key={brain.id}
                  className="flex items-center justify-between gap-3 p-4"
                  title={canToggle ? undefined : "Only this Boo’s admins can change this"}
                >
                  <p className="min-w-0 truncate text-sm">
                    {brain.name}
                    {brain.id === account.homeContainerId && (
                      <span className="ml-2 text-xs text-muted-foreground">Connected here</span>
                    )}
                  </p>
                  <Switch
                    checked={orgWide || booBinding !== undefined}
                    disabled={!canToggle || busy || orgWide}
                    onCheckedChange={() =>
                      booBinding ? unbind.mutate(booBinding.id) : bind.mutate(brain.id)
                    }
                  />
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Only while the home Boo is still around: with it gone there is no
          Boo to take the account private to. Read-only without canShare. */}
      {homeName !== undefined && (
        <div className="flex items-center justify-between gap-3 rounded-lg border p-4">
          <div className="min-w-0">
            <p className="text-sm font-medium">Shared with the workspace</p>
            <p className="text-xs text-muted-foreground">
              Connected from {homeName}.{" "}
              {account.canShare
                ? "Stop sharing to make it private to that Boo again."
                : "Its admins can stop sharing it."}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            disabled={busy || !account.canShare}
            onClick={() => setConfirmUnshare(true)}
          >
            Stop sharing
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmUnshare}
        onOpenChange={setConfirmUnshare}
        title="Stop sharing this account?"
        description={`It becomes private to ${homeName ?? "the Boo it was connected from"}. Every other Boo loses this account.`}
        confirmLabel="Stop sharing"
        loading={unshare.isPending}
        onConfirm={() => unshare.mutate()}
      />
    </div>
  );
}

// Tool access for the account. Stored per binding; edited here as one policy and
// applied to every binding the viewer may write — all of them when they manage
// the account, otherwise just the Boos they configure.
function ToolsTab({
  account,
  configurable,
  onChanged,
}: {
  account: HubAccountWithBindings;
  configurable: ReadonlySet<string>;
  onChanged: () => void;
}) {
  const writable = writableBindings(account, configurable);
  const template =
    writable.find((b) => b.containerId === null) ??
    writable[0] ??
    account.bindings.find((b) => b.containerId === null) ??
    account.bindings[0];
  const toolsQuery = useQuery({
    queryKey: ["hub-tools", account.app],
    queryFn: () => api.integrations.appTools(account.app),
  });

  if (account.bindings.length === 0) {
    return (
      <p className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
        Give a Boo access on the Access tab first, then choose which tools it can use.
      </p>
    );
  }
  if (toolsQuery.isLoading || toolsQuery.data === undefined) return <LoadingIndicator />;
  if (toolsQuery.isError) return <InlineError error={toolsQuery.error} />;
  if (toolsQuery.data.length === 0) {
    return <p className="text-sm text-muted-foreground">This app exposes no tools.</p>;
  }

  return (
    <ToolsEditor
      key={template.id}
      appLabel={account.appName ?? appName(account.app)}
      tools={toolsQuery.data}
      bindings={writable}
      partial={writable.length > 0 && writable.length < account.bindings.length}
      template={template}
      canManage={writable.length > 0}
      onChanged={onChanged}
    />
  );
}

function ToolsEditor({
  appLabel,
  tools,
  bindings,
  partial,
  template,
  canManage,
  onChanged,
}: {
  appLabel: string;
  tools: HubTool[];
  // The bindings a save writes to.
  bindings: IntegrationBinding[];
  // True when the viewer writes only some of the account's bindings.
  partial: boolean;
  template: IntegrationBinding;
  canManage: boolean;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();

  const [draftDefault, setDraftDefault] = useState<ToolState>(template.toolAccess.default);
  const [overrides, setOverrides] = useState<Record<string, ToolState>>(() => ({
    ...template.toolAccess.overrides,
  }));

  // A tool's baseline is the draft default, or "off" for a tool flagged
  // requiresExplicitEnable, so the default switch never moves those tools.
  // The read-only API row shows full access's state while full is on.
  const shown = (tool: HubTool): ToolState => displayedState(tool, tools, draftDefault, overrides);
  const { actions, apiAccess } = splitTools(tools);

  const draft: ToolAccess = minimalToolAccess(tools, draftDefault, overrides);
  // Compare minimal forms: the saved policy can carry overrides that equal a
  // tool's baseline (the backend sends raw-request's "off" explicitly).
  const dirty = toolAccessDiffers(
    draft,
    minimalToolAccess(tools, template.toolAccess.default, template.toolAccess.overrides),
  );

  const save = useMutation({
    // One policy → apply to every binding the viewer may write.
    mutationFn: () =>
      Promise.all(bindings.map((b) => api.integrations.updateBinding(b.id, { toolAccess: draft }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HUB_ACCOUNTS_KEY });
      toast.success("Tools updated");
      onChanged();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save tools"),
  });

  // Binary On/Off only — writes "on"/"off", never "ask" (parked).
  const setTool = (id: string, on: boolean) =>
    setOverrides((prev) => ({ ...prev, [id]: on ? "on" : "off" }));

  const onCount = actions.filter((t) => isOn(shown(t))).length;
  const copy = toolsEditorCopy(tools, appLabel);
  const footer = toolsFooterSummary(
    onCount,
    actions.length,
    apiAccess.length > 0 ? apiAccessLevel(tools, draftDefault, overrides) : null,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div>
        <p className="text-xs text-muted-foreground text-pretty">
          {copy.intro}
          {partial && " Your changes apply to the Boos you manage."}
        </p>
      </div>

      <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border bg-muted/30 p-4">
        <div className="min-w-0">
          <p className="text-sm font-medium">{copy.defaultTitle}</p>
          <p className="text-xs text-muted-foreground">{copy.defaultDescription}</p>
        </div>
        <Switch
          checked={isOn(draftDefault)}
          disabled={!canManage}
          onCheckedChange={(on) => setDraftDefault(on ? "on" : "off")}
        />
      </label>

      {actions.length > 0 && (
        <div className="divide-y rounded-lg border">
          {actions.map((tool) => (
            <label key={tool.id} className="flex cursor-pointer items-start justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="text-sm font-medium leading-tight">{tool.name}</p>
                {tool.description && (
                  <InlineMarkdown
                    text={tool.description}
                    className="mt-0.5 block text-xs text-muted-foreground text-pretty"
                  />
                )}
              </div>
              <Switch
                className="mt-0.5"
                checked={isOn(shown(tool))}
                disabled={!canManage}
                onCheckedChange={(on) => setTool(tool.id, on)}
              />
            </label>
          ))}
        </div>
      )}

      {apiAccess.length > 0 && (
        <div>
          <div className="px-1 pb-2">
            <p className="text-sm font-medium">{copy.apiHeading}</p>
            <p className="text-xs text-muted-foreground text-pretty">{copy.apiNote}</p>
          </div>
          <div className="divide-y rounded-lg border">
            {apiAccess.map((tool) => {
              const row = apiAccessRowCopy(tool, appLabel);
              // Read is locked on while full is on: keep the on colour, just not clickable.
              const included = readIncludedInFull(tool, tools, draftDefault, overrides);
              const locked = canManage && included;
              return (
                <label
                  key={tool.id}
                  className={cn(
                    "flex items-start justify-between gap-3 p-4",
                    included ? "cursor-default" : "cursor-pointer",
                  )}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-tight">{row.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground text-pretty">{row.description}</p>
                    {included && (
                      <p className="mt-1 text-xs font-medium text-muted-foreground">{READ_INCLUDED_HINT}</p>
                    )}
                  </div>
                  <div className="mt-0.5 flex shrink-0 items-center gap-1.5">
                    {locked && <Lock aria-hidden className="size-3.5 text-muted-foreground" />}
                    <Switch
                      className={cn(locked && "disabled:cursor-default disabled:opacity-80")}
                      checked={isOn(shown(tool))}
                      disabled={!canManage || included}
                      onCheckedChange={(on) => setTool(tool.id, on)}
                    />
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {canManage && (
        <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t bg-background/95 py-3 backdrop-blur supports-backdrop-filter:bg-background/80">
          <span className="text-xs text-muted-foreground tabular-nums">
            {footer}
          </span>
          <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
